import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchClergyBySlug } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Mail, Phone, Calendar, Heart, GraduationCap, 
  MapPin, Church, ArrowLeft, Quote, Shield 
} from 'lucide-react';
import SeoHead from '@/components/Seo';
import ImageHelper from '../lib/imageHelper';

interface Parish {
  id: number;
  name: string;
  slug: string;
  location?: string;
  archdeaconry?: { name: string; slug: string };
}

interface ClergyMember {
  id: number;
  title: string;
  name: string;
  slug: string;
  dob?: string | null;
  wedding?: string | null;
  photo?: string;
  hierarchy?: number;
  qualification?: string;
  hobby?: string;
  passion?: string;
  email?: string;
  phone?: string;
  biography?: string;
  parishes?: Parish[]; // Updated to plural array
  archdeaconry?: { name: string; slug: string };
}

const ClergyDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const [clergy, setClergy] = useState<ClergyMember | null>(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const loadClergy = async () => {
      try {
        const response = await fetchClergyBySlug(slug!);
        setClergy(response.data);
      } catch (error) {
        toast({ title: 'Error', description: 'Failed to fetch clergy details.', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };
    loadClergy();
  }, [slug, toast]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#fcfaf7]">
      <div className="h-10 w-10 border-4 border-amber-600/20 border-t-amber-600 rounded-full animate-spin"></div>
    </div>
  );

  if (!clergy) return <div className="p-10 text-center text-red-500 font-serif font-bold">Clergy not found.</div>;

  return (
    <div className="min-h-screen bg-[#fcfaf7] pb-20">
      <SeoHead
        title={`${clergy.title} ${clergy.name} | Ibadan South Diocese`}
        description={clergy.biography?.substring(0, 160) || "Clergy profile details."}
        ogImage={clergy.photo}
      />

      {/* Hero Header */}
      <div className="h-64 bg-slate-900 relative overflow-hidden">
        <img 
          src="https://res.cloudinary.com/dwrqyrm84/image/upload/v1750874183/home_ss1tdy.jpg" 
          className="w-full h-full object-cover opacity-30 grayscale"
          alt="texture"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#fcfaf7] to-transparent"></div>
      </div>

      <div className="max-w-6xl mx-auto px-4 -mt-32 relative z-10">
        <Link to="/clergy" className="inline-flex items-center text-white/80 hover:text-white mb-6 group transition-colors font-medium">
          <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
          Back to Directory
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          
          {/* Sidebar: Identity & Contact */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-white p-3 rounded-3xl shadow-xl ring-1 ring-slate-200">
              <div className="aspect-[3/4] rounded-2xl overflow-hidden mb-6 bg-slate-100">
                <ImageHelper 
                   src={clergy.photo} 
                   alt={clergy.name} 
                   className="w-full h-full object-cover" 
                />
              </div>
              <div className="px-4 pb-6">
                <h1 className="text-2xl font-serif font-bold text-slate-900 leading-tight">
                  {clergy.title} {clergy.name}
                </h1>
                
                {clergy.archdeaconry && (
                  <p className="text-amber-600 font-bold text-xs uppercase tracking-widest mt-2">
                    {clergy.archdeaconry.name}
                  </p>
                )}
                
                <div className="mt-8 space-y-4 pt-6 border-t border-slate-50">
                  {clergy.email && (
                    <a href={`mailto:${clergy.email}`} className="flex items-center text-slate-600 hover:text-amber-700 transition-colors group">
                      <div className="bg-slate-50 p-2 rounded-lg mr-3 group-hover:bg-amber-50 transition-colors"><Mail className="h-4 w-4" /></div>
                      <span className="text-sm truncate">{clergy.email}</span>
                    </a>
                  )}
                  {clergy.phone && (
                    <a href={`tel:${clergy.phone}`} className="flex items-center text-slate-600 hover:text-amber-700 transition-colors group">
                      <div className="bg-slate-50 p-2 rounded-lg mr-3 group-hover:bg-amber-50 transition-colors"><Phone className="h-4 w-4" /></div>
                      <span className="text-sm font-medium">{clergy.phone}</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-8 space-y-6">
            
            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
               {[
                 { label: 'Birthday', val: clergy.dob, icon: Calendar },
                 { label: 'Wedding', val: clergy.wedding, icon: Heart },
                 { label: 'Hobby', val: clergy.hobby, icon: GraduationCap },
                 { label: 'Passion', val: clergy.passion, icon: Quote }
               ].map((stat, i) => stat.val && (
                 <div key={i} className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100">
                   <stat.icon className="h-4 w-4 text-amber-600 mb-2" />
                   <p className="text-[10px] uppercase font-bold text-slate-400 tracking-tighter">{stat.label}</p>
                   <p className="text-slate-800 font-bold text-sm truncate">{stat.val}</p>
                 </div>
               ))}
            </div>

            {/* Amber Station Card - Handles Multiple Parishes */}
            {clergy.parishes && clergy.parishes.length > 0 && (
              <div className="bg-amber-600 text-white rounded-3xl p-6 shadow-lg shadow-amber-600/20">
                <div className="flex items-center gap-3 mb-4 opacity-90">
                  <Shield className="h-5 w-5" />
                  <p className="text-xs font-bold uppercase tracking-[0.2em]">Active Assignments</p>
                </div>
                
                <div className="space-y-4">
                  {clergy.parishes.map((p, idx) => (
                    <div key={p.id} className={`flex flex-col md:flex-row md:items-center justify-between gap-4 ${idx !== 0 ? 'pt-4 border-t border-white/20' : ''}`}>
                      <div className="flex items-start gap-4">
                        <div className="bg-white/20 p-3 rounded-2xl backdrop-blur-sm shrink-0">
                          <Church className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <Link to={`/parishes/${p.slug}`} className="font-serif text-xl font-bold hover:underline decoration-white/30 decoration-2 underline-offset-4">
                            {p.name}
                          </Link>
                          {p.archdeaconry && (
                            <p className="text-[10px] font-medium uppercase opacity-70 mt-0.5">{p.archdeaconry.name}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm bg-black/10 px-4 py-2 rounded-xl self-start md:self-center backdrop-blur-sm">
                        <MapPin className="h-3 w-3" />
                        <span className="opacity-90">{p.location || 'Ibadan'}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Biography & Qualifications Card */}
            <Card className="border-none shadow-sm rounded-3xl overflow-hidden bg-white">
              <CardContent className="p-8 md:p-12">
                <div className="flex items-center gap-4 mb-8">
                  <h2 className="text-2xl font-serif font-bold text-slate-800 shrink-0">Profile & Biography</h2>
                  <div className="h-[1px] flex-1 bg-slate-100"></div>
                </div>

                {clergy.qualification && (
                  <div className="mb-10 p-5 bg-slate-50 rounded-2xl border border-slate-100 flex items-start gap-4">
                    <div className="bg-white p-2 rounded-lg shadow-sm">
                        <GraduationCap className="h-5 w-5 text-amber-600" />
                    </div>
                    <div>
                      <span className="font-bold uppercase text-[10px] text-slate-400 block tracking-widest mb-1">Academic Qualifications</span>
                      <p className="text-slate-700 font-medium italic font-serif leading-relaxed">
                        {clergy.qualification}
                      </p>
                    </div>
                  </div>
                )}

                <div className="prose prose-slate max-w-none">
                  {clergy.biography ? (
                    <div
                      className="whitespace-pre-wrap text-slate-600 leading-relaxed text-lg font-serif"
                      dangerouslySetInnerHTML={{ __html: clergy.biography.replace(/\r?\n/g, '<br/>') }}
                    />
                  ) : (
                    <div className="py-12 text-center bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                        <Quote className="h-8 w-8 text-slate-200 mx-auto mb-3" />
                        <p className="text-slate-400 italic font-serif">A detailed biography for {clergy.title} {clergy.name} is currently being prepared by the Diocesan communication office.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ClergyDetail;